import RowStack from '@common-components/common/row-stack/RowStack';
import { UserMessage } from '@bt-common/schemas/bt/user-message';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import { useEffect, useState } from 'react';

type Props = {
    height: string;
    scheduledMessage?: UserMessage;
}

export default function ReplyToMessageChip({ height, scheduledMessage }: Props) {
    const messageReplyData = useAppSelector((state) => state.AddScheduledMessageButtonReducer.message.replyData);
    const localReplyData = useAppSelector(state => state.WhatsAppReducer.isReply)

    const [replyData, setReplyData] = useState(scheduledMessage?.replyData ?? messageReplyData ?? localReplyData)

    const bblColor = replyData?.fromMe ? '#06cf9c' : 'var(--color-accent)'
    const sender = replyData?.fromMe ? 'You' : replyData?.sender

    const base64Format = `data:${replyData?.mimetype};base64,${replyData?.data}`

    useEffect(() => {
        if (!scheduledMessage) {
            if (localReplyData) {
                setReplyData(localReplyData)
            }
            if (messageReplyData) {
                setReplyData(messageReplyData)
            }
        }
    }, [messageReplyData, localReplyData])

    // Function to render media content based on type
    const renderMediaContent = () => {
        if (!replyData?.mimetype || !replyData?.data) return null;
        
        const type = replyData.mimetype.toLowerCase();
        
        if (type.includes('image')) {
            return (
                <img 
                    src={base64Format} 
                    alt={replyData?.filename ?? 'image'} 
                    style={{
                        height,
                        objectFit: 'cover',
                        borderTopRightRadius: '7px',
                        borderBottomRightRadius: '7px',
                    }} 
                />
            );
        } else if (type.includes('video')) {
            return (
                <video 
                    src={base64Format}
                    style={{
                        height,
                        objectFit: 'cover',
                        borderTopRightRadius: '7px',
                        borderBottomRightRadius: '7px',
                    }}
                    muted
                    preload="metadata"
                />
            );
        } else if (type === 'application/pdf') {
            return (
                <div style={{
                    width: '120px',
                    height,
                    overflow: 'hidden',
                    borderTopRightRadius: '7px',
                    borderBottomRightRadius: '7px',
                }}>
                    <iframe
                        src={base64Format}
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            borderRadius: 'inherit',
                        }}
                        title="PDF Preview"
                    />
                </div>
            );
        } else {
            // For audio and other file types, show icon with filename
            const getFileIcon = () => {
                if (type.includes('audio') || type === 'application/ogg') {
                    return <AudioFileIcon sx={{ fontSize: 16, color: '#667781' }} />;
                }
                return <AttachFileIcon sx={{ fontSize: 16, color: '#667781' }} />;
            };

            return (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '0 12px',
                    height,
                    minWidth: '0',
                }}>
                    {getFileIcon()}
                    <span style={{
                        fontSize: '12px',
                        color: '#667781',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '80px',
                    }}>
                        {replyData?.filename || 'File'}
                    </span>
                </div>
            );
        }
    };

    return (
        <>
            {replyData &&
                <RowStack sx={{
                    width: '100%',
                    backgroundColor: '#0b141a0d',
                    borderRadius: '7px',
                    overflow: 'hidden',
                }}>
                    <span style={{
                        backgroundColor: bblColor,
                        flex: 'none',
                        width: '4px',
                        height,
                        borderTopLeftRadius: '7px',
                        borderBottomLeftRadius: '7px',
                    }}></span>

                    <div style={{
                        height,
                        padding: '7px 12px 10px 11px',
                        display: 'flex',
                        flexGrow: 1,
                        alignItems: 'center',
                        overflow: 'hidden',
                        boxSizing: 'border-box',
                    }}>
                        <div style={{
                            flexGrow: 1,
                            overflow: 'hidden',
                        }}>
                            <span style={{
                                display: 'inline-flex',
                                maxWidth: '100%',
                                fontSize: '12.8px',
                                lineHeight: '22px',
                                fontWeight: 500,
                                color: bblColor,
                            }}>{sender}</span>

                            <span style={{
                                display: '-webkit-box',
                                maxHeight: '40px',
                                overflow: 'hidden',
                                fontSize: '13px',
                                lineHeight: '22px',
                                color: '#667781',
                                textOverflow: 'ellipsis',
                                wordWrap: 'break-word',
                                whiteSpace: 'pre-wrap',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                            }}>{replyData?.text || (replyData?.mimetype ? replyData?.filename || 'Media file' : '')}</span>
                        </div>
                    </div>

                    {renderMediaContent()}
                </RowStack>
            }
        </>
    )
}